import { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { CustomLoader } from "../../components/ui/custom-loader";
import {
  donationAPI,
  fundingStatusAPI,
  type DonationDetail,
  type FundingStatusData,
} from "../../services/api";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import { BarChart3, DollarSign, TrendingUp } from "lucide-react";

const CHART_COLORS = [
  "hsl(var(--primary))",
  "hsl(var(--accent))",
  "#22c55e",
  "#f59e0b",
  "#6366f1",
  "#ec4899",
];

const DONATION_TYPE_COLORS: Record<string, string> = {
  education: "hsl(var(--primary))",
  standard: "hsl(var(--primary) / 0.78)",
  infrastructure: "hsl(var(--primary) / 0.56)",
};

function buildMonthlyTrendData(donations: DonationDetail[]) {
  const map = new Map<number, { month: string; amount: number }>();

  donations.forEach(donation => {
    try {
      const date = new Date(donation.created_at);
      const sortKey = date.getFullYear() * 12 + date.getMonth();
      const monthLabel = date.toLocaleString("en-IN", {
        month: "short",
        year: "numeric",
      });
      const amount = parseFloat(donation.amount || "0");
      const existing = map.get(sortKey);

      if (existing) {
        existing.amount += amount;
      } else {
        map.set(sortKey, { month: monthLabel, amount });
      }
    } catch {
      // skip invalid dates
    }
  });

  return Array.from(map.entries())
    .sort((a, b) => a[0] - b[0])
    .slice(-12)
    .map(([, value]) => ({ month: value.month, amount: value.amount }));
}

export default function DonationReports() {
  const [fundingStatus, setFundingStatus] = useState<FundingStatusData | null>(
    null
  );
  const [donations, setDonations] = useState<DonationDetail[]>([]);
  const [totalDonationCount, setTotalDonationCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchReportData() {
      setLoading(true);
      setError(null);

      try {
        const [status, donationsResult] = await Promise.all([
          fundingStatusAPI.get(),
          donationAPI.getDonationDetails({ page: 1, page_size: 5000 }),
        ]);

        if (cancelled) {
          return;
        }

        setFundingStatus(status);
        setDonations(donationsResult.donations);
        setTotalDonationCount(donationsResult.count);
      } catch (e) {
        if (!cancelled) {
          setError(
            e instanceof Error ? e.message : "Failed to load donation reports"
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void fetchReportData();
    return () => {
      cancelled = true;
    };
  }, []);

  const totalFunding = fundingStatus?.total_funding ?? 0;
  const byType = useMemo(
    () => fundingStatus?.by_type ?? [],
    [fundingStatus?.by_type]
  );

  const pieChartData = useMemo(() => {
    if (!byType.length) {
      return [];
    }

    return byType.map((item, index) => ({
      name: item.donation_type,
      value: item.percentage,
      amount: item.amount,
      color:
        DONATION_TYPE_COLORS[item.donation_type.toLowerCase()] ??
        CHART_COLORS[index % CHART_COLORS.length],
    }));
  }, [byType]);

  const monthlyTrendData = useMemo(
    () => buildMonthlyTrendData(donations),
    [donations]
  );

  if (loading) {
    return <CustomLoader fullScreen />;
  }

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="flex items-center gap-2 text-3xl font-bold text-foreground">
          <BarChart3 className="h-8 w-8 text-primary" />
          Donation Reports
        </h1>
        <p className="text-muted-foreground">
          Monitor funding distribution and monthly donation trends
        </p>
      </div>

      {error && (
        <Card className="border-destructive/50">
          <CardContent className="py-4 text-sm text-destructive">
            {error}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <DollarSign className="h-4 w-4" />
              Total Donation
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">
              ₹{totalFunding.toLocaleString("en-IN")}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <TrendingUp className="h-4 w-4" />
              Total Transactions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">
              {totalDonationCount.toLocaleString("en-IN")}
            </div>
          </CardContent>
        </Card>

        {byType.map(item => (
          <Card key={item.donation_type}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium capitalize text-muted-foreground">
                {item.donation_type}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-foreground">
                ₹{Number(item.amount).toLocaleString("en-IN")}
              </div>
              <p className="text-sm text-muted-foreground">
                {item.percentage}% of total
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Funding Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            {pieChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pieChartData}
                    cx="50%"
                    cy="50%"
                    labelLine
                    label={({ name, value }) =>
                      `${name} ${Number(value).toFixed(2)}%`
                    }
                    outerRadius={92}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {pieChartData.map(entry => (
                      <Cell key={`cell-${entry.name}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(
                      value: number,
                      _name: string,
                      props: { payload?: { amount?: number } }
                    ) => [
                      `${value}%`,
                      props?.payload?.amount !== undefined
                        ? `₹${Number(props.payload.amount).toLocaleString("en-IN")}`
                        : "",
                    ]}
                  />
                  <Legend
                    iconType="circle"
                    formatter={value => (
                      <span className="capitalize text-foreground">
                        {value}
                      </span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-[300px] items-center justify-center text-muted-foreground">
                No distribution data
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Monthly Funding Trend</CardTitle>
          </CardHeader>
          <CardContent>
            {monthlyTrendData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={monthlyTrendData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis
                    tickFormatter={value =>
                      value >= 1000
                        ? `₹${(value / 1000).toFixed(0)}K`
                        : `₹${value}`
                    }
                  />
                  <Tooltip
                    formatter={(value: number) => [
                      `₹${Number(value).toLocaleString("en-IN")}`,
                      "Amount",
                    ]}
                  />
                  <Line
                    type="monotone"
                    dataKey="amount"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-[300px] items-center justify-center text-muted-foreground">
                No trend data
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
